import {
  collection,
  getDocs,
  query,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../firebase';
import { EquipmentTypeSuggestion } from '../types/firestore';

interface SubmitEquipmentTypeSuggestionInput {
  suggestedName: string;
  notes?: string;
}

interface SubmitEquipmentTypeSuggestionResponse {
  success: boolean;
  suggestionId: string;
}

const EQUIPMENT_TYPE_SUGGESTIONS_COLLECTION = 'equipmentTypeSuggestions';

const asTrimmedString = (value: unknown): string =>
  typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '';

const mapSuggestionDoc = (
  docSnap: QueryDocumentSnapshot<DocumentData>
): EquipmentTypeSuggestion | null => {
  const data = docSnap.data() as Record<string, unknown>;
  const suggestedName = asTrimmedString(data.suggestedName);

  const status = asTrimmedString(data.status).toLowerCase();
  const normalizedStatus: EquipmentTypeSuggestion['status'] =
    status === 'accepted' || status === 'rejected' ? status : 'pending';

  return {
    uid: docSnap.id,
    id: docSnap.id,
    equipmentTypeSuggestionId: asTrimmedString(data.equipmentTypeSuggestionId) || docSnap.id,
    suggestedBy: asTrimmedString(data.suggestedBy),
    suggestedByDisplayName: asTrimmedString(data.suggestedByDisplayName) || null,
    suggestedByRole:
      asTrimmedString(data.suggestedByRole) === 'serwisant'
        ? 'serwisant'
        : asTrimmedString(data.suggestedByRole) === 'serviceAdmin'
          ? 'serviceAdmin'
          : null,
    suggestedName,
    suggestedNameKey: asTrimmedString(data.suggestedNameKey),
    notes: asTrimmedString(data.notes) || null,
    status: normalizedStatus,
    source: 'serviceAdmin',
    createdAt: data.createdAt as EquipmentTypeSuggestion['createdAt'],
    updatedAt: data.updatedAt as EquipmentTypeSuggestion['updatedAt'],
    resolvedAt: data.resolvedAt as EquipmentTypeSuggestion['resolvedAt'],
    resolvedBy: asTrimmedString(data.resolvedBy) || null,
    canonicalEquipmentTypeId: asTrimmedString(data.canonicalEquipmentTypeId) || null,
    canonicalName: asTrimmedString(data.canonicalName) || null,
    canonicalShortCode: asTrimmedString(data.canonicalShortCode) || null,
    rejectionReason: asTrimmedString(data.rejectionReason) || null,
  };
};

export const submitEquipmentTypeSuggestion = async (
  input: SubmitEquipmentTypeSuggestionInput
): Promise<SubmitEquipmentTypeSuggestionResponse> => {
  const submitEquipmentTypeSuggestionFn = httpsCallable<
    SubmitEquipmentTypeSuggestionInput,
    SubmitEquipmentTypeSuggestionResponse
  >(functions, 'submitEquipmentTypeSuggestion');

  const result = await submitEquipmentTypeSuggestionFn({
    suggestedName: String(input.suggestedName || '').trim(),
    ...(typeof input.notes === 'string' ? { notes: input.notes } : {}),
  });

  if (!result.data?.suggestionId) {
    throw new Error('Backend nie zwrócił identyfikatora sugestii.');
  }

  return result.data;
};

export const getEquipmentTypeSuggestionsForCurrentUser = async (
  userId: string
): Promise<EquipmentTypeSuggestion[]> => {
  const normalizedUserId = String(userId || '').trim();
  if (!normalizedUserId) return [];

  const snapshot = await getDocs(
    query(
      collection(firestore, EQUIPMENT_TYPE_SUGGESTIONS_COLLECTION),
      where('suggestedBy', '==', normalizedUserId)
    )
  );

  return snapshot.docs
    .map((docSnap) => mapSuggestionDoc(docSnap))
    .filter((entry): entry is EquipmentTypeSuggestion => Boolean(entry))
    .sort(
      (a, b) =>
        (b.createdAt?.toDate?.()?.getTime?.() || 0) - (a.createdAt?.toDate?.()?.getTime?.() || 0)
    );
};

export const getPendingEquipmentTypeSuggestionsForAdmin = async (): Promise<
  EquipmentTypeSuggestion[]
> => {
  const snapshot = await getDocs(
    query(
      collection(firestore, EQUIPMENT_TYPE_SUGGESTIONS_COLLECTION),
      where('status', '==', 'pending')
    )
  );

  return snapshot.docs
    .map((docSnap) => mapSuggestionDoc(docSnap))
    .filter((entry): entry is EquipmentTypeSuggestion => Boolean(entry))
    .sort(
      (a, b) =>
        (b.createdAt?.toDate?.()?.getTime?.() || 0) - (a.createdAt?.toDate?.()?.getTime?.() || 0)
    );
};
